import { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  HelpCircle,
  LogOut,
  Menu,
  User as UserIcon,
  X,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTour } from "../hooks/useTour";
import { useSidebarStore } from "../stores/sidebarStore";
import { SIDEBAR_HIDDEN_PATHS } from "../constants/layout";
import ConfirmDialog from "./ConfirmDialog";
import PlanCreditBadge from "./billing/PlanCreditBadge";
import NotificationsPanel from "./NotificationsPanel";

const HIDDEN_HEADER_PAGES = ["/login", "/register"];
// const SIDEBAR_HIDDEN_PAGES = [
//   "/",
//   "/privacy-policy",
//   "/terms-and-conditions",
//   "/refund-policy",
//   "/privacy",
//   "/terms",
//   "/terms-of-service",
// ];

// ── Section definitions — keyed by data-section attribute ────────────────
const LANDING_SECTIONS: { id: string; dark: boolean }[] = [
  { id: "hero", dark: false },
  { id: "features", dark: false },
  { id: "social", dark: false },
  { id: "howitworks", dark: false },
  { id: "templates", dark: false },
  { id: "ats", dark: true },
  { id: "pricing", dark: false },
  { id: "blog", dark: false },
  { id: "company", dark: true },
  { id: "faq", dark: false },
  { id: "cta", dark: true },
];

// ── Nav items shown in the header centre ────────────────────────────────
const LANDING_NAV = [
  { label: "Proven Success", sectionId: "social" },
  { label: "How It Works", sectionId: "howitworks" },
  { label: "Templates", sectionId: "templates" },
  { label: "Blog", sectionId: "blog" },
  { label: "FAQ", sectionId: "faq" },
];

export default function AppHeader() {
  const { user, isLoading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCollapsed = useSidebarStore((state) => state.isCollapsed);
  const isSidebarHidden = useSidebarStore((state) => state.isSidebarHidden);
  const closeMobile = useSidebarStore((state) => state.closeMobile);
  const { startTour, startBuilderTour, resetTour, isTourActive } = useTour(
    user?.role === "admin",
    user?.id,
  );

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Scroll-aware state
  const [isDark, setIsDark] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("hero");

  const profileRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const isLanding = location.pathname === "/";
  const isHidden = HIDDEN_HEADER_PAGES.includes(location.pathname);
  const showSidebar =
    user &&
    !isLoading &&
    !SIDEBAR_HIDDEN_PATHS.includes(location.pathname) &&
    !isSidebarHidden;
  const isCvBuilderRoute =
    location.pathname.startsWith("/cv-builder") ||
    location.pathname.startsWith("/editor");
  const isAiAssistantRoute = location.pathname.startsWith("/ai-assistant");
  const isAiCvBuilderRoute = location.pathname.startsWith("/ai-cv-builder");
  const isImmersiveRoute =
    isCvBuilderRoute || isAiAssistantRoute || isAiCvBuilderRoute;

  const headerOffset = showSidebar
    ? isCollapsed
      ? "md:pl-20"
      : "md:pl-72"
    : "pl-0";

  // ── Scroll to section by data-section id ────────────────────────────
  const scrollToSection = useCallback(
    (sectionId: string) => {
      setMobileNavOpen(false);
      const doScroll = () => {
        const el = document.querySelector(`[data-section="${sectionId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };

      if (!isLanding) {
        navigate("/");
        setTimeout(doScroll, 700);
      } else {
        doScroll();
      }
    },
    [isLanding, navigate],
  );

  // ── IntersectionObserver on landing — uses data-section attr ────────
  useEffect(() => {
    if (!isLanding) {
      setIsDark(false);
      setActiveSectionId("hero");
      return;
    }

    // Disconnect previous observer
    observerRef.current?.disconnect();

    const attach = () => {
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const id = (entry.target as HTMLElement).dataset.section ?? "";
            const def = LANDING_SECTIONS.find((s) => s.id === id);
            if (!def) return;
            setActiveSectionId(id);
            setIsDark(def.dark);
          });
        },
        { rootMargin: "-50% 0px -49% 0px", threshold: 0 },
      );

      LANDING_SECTIONS.forEach(({ id }) => {
        const el = document.querySelector(`[data-section="${id}"]`);
        if (el) obs.observe(el);
      });

      observerRef.current = obs;
    };

    attach();
    // Retry after content renders (loader delay)
    const t = setTimeout(attach, 1000);
    return () => {
      clearTimeout(t);
      observerRef.current?.disconnect();
    };
  }, [isLanding, location.pathname]);

  // ── Scroll detection ────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Existing side effects ────────────────────────────────────────────
  useEffect(() => {
    if (!showSidebar) closeMobile();
    setIsProfileOpen(false);
    setMobileNavOpen(false);
  }, [showSidebar, location.pathname, closeMobile]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(e.target as Node)
      )
        setIsProfileOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (isHidden) return null;

  /**
   * Quick access to the guided tour from anywhere in the app. Inside the CV
   * builder this walks whatever the builder is showing right now, in place —
   * restarting the whole create-a-resume journey would drag someone away
   * from a CV they're already editing just to relive the template picker.
   */
  const handleQuickTour = () => {
    if (isTourActive) return;

    if (isCvBuilderRoute) {
      void startBuilderTour();
      return;
    }

    resetTour();
    navigate(user?.role === "admin" ? "/dashboard" : "/home");
    setTimeout(() => {
      startTour();
    }, 500);
  };

  const handleLogoutClick = () => {
    setIsProfileOpen(false);
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
    } catch {
      // ignore — navigate regardless
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
      navigate("/login");
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
  };

  // ── Theme tokens ─────────────────────────────────────────────────────
  // On the landing page the bar is a transparent overlay across the hero
  // until you scroll, then it detaches from the top edge and shrinks into a
  // centred floating pill. Everywhere else it stays a plain fixed bar.
  const isLandingPill = isLanding && scrolled;

  const landingBg = scrolled
    ? isDark
      ? "rgba(6,6,10,0.90)"
      : "rgba(255,255,255,0.94)"
    : // Nothing at all over the hero: the bar reads as part of the hero
      // rather than a panel sitting on it. Written as a zero-alpha white
      // instead of the keyword `transparent` so it interpolates into the
      // scrolled colour, same reasoning as landingShadow below.
      "rgba(255,255,255,0)";
  const landingBorder = scrolled
    ? isDark
      ? "rgba(129,140,248,0.14)"
      : "rgba(0,0,0,0.07)"
    : "transparent";
  const landingShadow = scrolled
    ? isDark
      ? "0 4px 32px rgba(0,0,0,0.5)"
      : "0 4px 24px rgba(0,0,0,0.06)"
    : "0 4px 24px rgba(0,0,0,0)";
  // Same reasoning as landingShadow above: a real zero-strength filter at
  // rest, not the keyword none, so it can tween into the scrolled value.
  const landingBlur = scrolled
    ? "blur(16px) saturate(180%)"
    : "blur(0px) saturate(100%)";

  const logoTextColor = isLanding && isDark ? "#ffffff" : "#0f0f14";
  const logoXColor = "#6366f1";
  const navLinkColor =
    isLanding && isDark ? "rgba(255,255,255,0.52)" : "rgba(15,15,20,0.52)";
  const loginColor = isLanding && isDark ? "rgba(255,255,255,0.6)" : "#374151";

  const avatarFallback =
    user?.name?.trim().charAt(0) || user?.email?.charAt(0) || "U";
  const hasProfileImage =
    user?.profileImageUrl && user.profileImageUrl.trim() !== "";

  return (
    <>
      <style>{`
        /* Nav link */
        .ah-link {
          padding: 6px 12px; border-radius: 9px; font-size: 13px; font-weight: 600;
          cursor: pointer; border: none; background: none; white-space: nowrap;
          transition: color 0.25s, background 0.25s; letter-spacing: 0.01em;
          text-decoration: none; display: inline-flex; align-items: center;
        }
        .ah-link:hover { background: rgba(99,102,241,0.08); }
        .ah-link.act-dark  { color: #c7d2fe !important; background: rgba(99,102,241,0.14); }
        .ah-link.act-light { color: #6366f1 !important; background: rgba(99,102,241,0.08); }

        /* Underline */
        .ah-wrap { position: relative; }
        .ah-wrap::after {
          content: ''; position: absolute; bottom: -1px; left: 50%; right: 50%;
          height: 2px; border-radius: 2px;
          background: linear-gradient(90deg, #6366f1, #8b5cf6);
          transition: left 0.3s, right 0.3s;
        }
        .ah-wrap.act::after { left: 12px; right: 12px; }

        /* Register CTA */
        .ah-cta {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 18px; border-radius: 100px; font-size: 13px; font-weight: 700;
          background: linear-gradient(135deg, #6366f1, #7c3aed);
          color: #fff; border: none; cursor: pointer;
          box-shadow: 0 4px 16px rgba(99,102,241,0.3);
          transition: transform 0.25s, box-shadow 0.25s;
          text-decoration: none; white-space: nowrap;
        }
        .ah-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(99,102,241,0.45); }
        .ah-cta .arr { transition: transform 0.25s; }
        .ah-cta:hover .arr { transform: translateX(3px); }

        /* Mobile drawer */
        .ah-drawer {
          position: fixed; top: 72px; left: 0; right: 0; z-index: 49;
          padding: 10px 14px 20px; border-bottom: 1px solid rgba(129,140,248,0.1);
          transform: translateY(-10px); opacity: 0; pointer-events: none;
          transition: transform 0.3s cubic-bezier(0.34,1.2,0.64,1), opacity 0.3s;
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          max-height: calc(100vh - 72px); overflow-y: auto;
        }
        .ah-drawer.open { transform: translateY(0); opacity: 1; pointer-events: auto; }
        .ah-drawer-link {
          display: block; width: 100%; text-align: left;
          padding: 10px 12px; border-radius: 10px; font-size: 14px; font-weight: 600;
          cursor: pointer; border: none; background: none;
          transition: background 0.2s; text-decoration: none;
        }
        .ah-drawer-link:hover { background: rgba(99,102,241,0.08); }
        .ah-divider {
          height: 1px; margin: 8px 0;
          background: linear-gradient(90deg, transparent, rgba(129,140,248,0.2), transparent);
        }
        .ah-drawer-cta {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          width: 100%; padding: 12px; border-radius: 100px; font-size: 14px; font-weight: 700;
          background: linear-gradient(135deg, #6366f1, #7c3aed); color: #fff;
          border: none; cursor: pointer; margin-top: 6px;
          box-shadow: 0 4px 20px rgba(99,102,241,0.3); text-decoration: none;
        }
      `}</style>

      {/* ── Header ── */}
      <header
        className={`app-header fixed top-0 right-0 left-0 w-full z-40 transition-all duration-300 ease-in-out ${headerOffset} ${
          // Keeps the pill off the screen edges on narrow viewports, where
          // max-w-5xl doesn't constrain it.
          isLandingPill ? "px-2 sm:px-3" : ""
        }`}
        style={
          isLanding
            ? undefined
            : {
                borderBottom: "1px solid rgb(243,244,246)",
                background: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }
        }
      >
        <div
          className={`mx-auto flex items-center justify-between transition-all duration-300 ease-in-out ${
            isLandingPill
              ? "mt-3 h-[66px] max-w-[96rem] rounded-full px-5 sm:px-7"
              : "max-w-full h-[72px] px-4 sm:px-6 lg:px-8"
          }`}
          style={
            isLanding
              ? {
                  background: landingBg,
                  // Always a full 1px solid border, on every side, in both
                  // states — only its colour animates (transparent at rest,
                  // a real colour once scrolled). Flipping between
                  // border-bottom-only and border-all-sides, or between
                  // style:none and style:solid, are both non-interpolable
                  // jumps for the same reason boxShadow's `none` is below.
                  border: `1px solid ${landingBorder}`,
                  boxShadow: landingShadow,
                  backdropFilter: landingBlur,
                  WebkitBackdropFilter: landingBlur,
                }
              : undefined
          }
        >
          {/* Logo — always shown for the builder (same mark as the sidebar);
              elsewhere it hides on desktop once the expanded sidebar is
              already showing it. */}
          <div
            className={`flex items-center gap-6 flex-shrink-0 ${
              !isSidebarHidden && showSidebar && !isCollapsed ? "md:hidden" : ""
            }`}
          >
            <Link
              to="/"
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "20px",
                  fontWeight: 900,
                  letterSpacing: "-0.03em",
                  color: logoTextColor,
                  transition: "color 0.3s",
                }}
              >
                Resume<span style={{ color: logoXColor }}>X</span>
              </span>
            </Link>
          </div>

          {/* Page-owned header controls, centred — the CV builder portals its
              resume/design/layout pickers and action buttons in here (see
              BuilderHeaderActions). Absolutely positioned against the fixed
              header so it centres independent of the logo/right-side widths. */}
          <div
            id="app-header-left-actions"
            className="absolute left-1/2 flex -translate-x-1/2 items-center"
          />

          {/* Centre nav — landing + logged out only, desktop */}
          {isLanding && !user && (
            <nav className="hidden lg:flex items-center gap-0.5 absolute left-1/2 -translate-x-1/2">
              {LANDING_NAV.map((item) => {
                const isAct = activeSectionId === item.sectionId;
                return (
                  <div
                    key={item.label}
                    className={`ah-wrap ${isAct ? "act" : ""}`}
                  >
                    <button
                      className={`ah-link ${isAct ? (isDark ? "act-dark" : "act-light") : ""}`}
                      style={{ color: isAct ? undefined : navLinkColor }}
                      onClick={() => scrollToSection(item.sectionId)}
                    >
                      {item.label}
                    </button>
                  </div>
                );
              })}
            </nav>
          )}

          {/* Right side */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Page-owned controls beside the plan pill — the AI CV builder
                portals its "Save & exit" in here (see AgentHeaderActions).
                Hidden while empty, so it adds no gap on other pages. */}
            <div
              id="app-header-right-actions"
              className="flex items-center empty:hidden"
            />

            {/* Plan + remaining download credits; links through to pricing */}
            {user && !isLoading && !isLanding && <PlanCreditBadge />}

            {/* Quick access to the guided tour — same "app-level" spot as
                the notification bell, always reachable, not tucked away in
                Profile settings. */}
            {user && !isLoading && !isLanding && (
              <button
                type="button"
                onClick={handleQuickTour}
                disabled={isTourActive}
                className="rounded-lg p-2 text-gray-600 transition-all hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label={
                  isCvBuilderRoute ? "Learn this page" : "Take a guided tour"
                }
                title={
                  isCvBuilderRoute ? "Learn this page" : "Take a guided tour"
                }
              >
                <HelpCircle className="h-5 w-5" />
              </button>
            )}

            {/* Blog link — only when logged in (inside app) */}
            {/* {user && !isLoading && (
              <Link
                to="/blog"
                className="hidden sm:flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
              >
                <BookOpen className="h-4 w-4" />
                Blog
              </Link>
            )} */}

            {/* Bell — live feed (articles, offers, designs, billing, CV
                scores), only inside the app, NOT on landing */}
            {user && !isLoading && !isLanding && <NotificationsPanel />}

            {/* User avatar / auth buttons */}
            {isLoading ? (
              <div className="h-8 w-8 animate-pulse rounded-full bg-gray-200" />
            ) : user ? (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setIsProfileOpen((p) => !p)}
                  className="flex items-center gap-1.5 rounded-xl px-2 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-all"
                >
                  {hasProfileImage && (
                    <img
                      className="h-8 w-8 rounded-full object-cover border border-gray-200"
                      src={user.profileImageUrl}
                      alt={user.name || "Profile"}
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        (
                          e.currentTarget.nextElementSibling as HTMLElement
                        )?.classList.remove("hidden");
                      }}
                    />
                  )}
                  <span
                    className={`flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white ${hasProfileImage ? "hidden" : ""}`}
                  >
                    {avatarFallback.toUpperCase()}
                  </span>
                  {/* <ChevronDown
                    className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-200 ${isProfileOpen ? "rotate-180" : ""}`}
                  /> */}
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-3 w-72 origin-top-right rounded-2xl border border-gray-100 bg-white shadow-[0_12px_40px_rgba(0,0,0,0.08)] z-50 overflow-hidden transform transition-all">

                    {/* User info header - Added a subtle background and better spacing */}
                    <div className="flex items-center gap-3.5 border-b border-gray-100 bg-slate-50/80 px-5 py-4">
                      {hasProfileImage ? (
                        <img
                          className="h-11 w-11 shrink-0 rounded-full object-cover border-2 border-white shadow-sm"
                          src={user.profileImageUrl}
                          alt={user.name || "Profile"}
                        />
                      ) : (
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-sm border-2 border-white">
                          {avatarFallback.toUpperCase()}
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-extrabold text-gray-900 tracking-tight">
                          {user.name || "User"}
                        </p>
                        <p className="truncate text-xs font-medium text-gray-500 mt-0.5">
                          {user.email}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="p-2">
                      <Link
                        to="/profile"
                        onClick={() => setIsProfileOpen(false)}
                        /* Using 'group' to light up the icon when the row is hovered */
                        className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-all duration-200"
                        style={{ textDecoration: "none" }}
                      >
                        <UserIcon className="h-4 w-4 text-gray-400 group-hover:text-indigo-600 transition-colors duration-200" />
                        View Profile
                      </Link>

                      {/* Subtle divider between profile and destructive actions */}
                      <div className="my-1 mx-3 h-px bg-gray-100" />

                      <button
                        type="button"
                        onClick={handleLogoutClick}
                        /* Opens confirmation dialog before executing logout */
                        className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-gray-700 hover:bg-rose-50 hover:text-rose-600 transition-all duration-200"
                      >
                        <LogOut className="h-4 w-4 text-gray-400 group-hover:text-rose-500 transition-colors duration-200" />
                        Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Desktop auth */}
                <Link
                  to="/login"
                  className="hidden sm:inline text-sm font-semibold transition-colors duration-200 hover:opacity-70"
                  style={{ color: loginColor, textDecoration: "none" }}
                >
                  Log in
                </Link>
                <Link to="/register" className="ah-cta hidden sm:inline-flex">
                  Get Start
                </Link>

                {/* Mobile hamburger — landing only */}
                {isLanding && (
                  <button
                    className="lg:hidden flex items-center justify-center w-9 h-9 border-none cursor-pointer transition-colors"
                    style={{
                      color: isDark ? "rgba(255,255,255,0.7)" : "#374151",
                      background: "none",
                      borderRadius: "10px",
                    }}
                    onClick={() => setMobileNavOpen((o) => !o)}
                    aria-label="Toggle menu"
                  >
                    {mobileNavOpen ? <X size={20} /> : <Menu size={20} />}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile drawer (landing, logged-out) ── */}
      {isLanding && !user && (
        <>
          <div
            className={`ah-drawer ${mobileNavOpen ? "open" : ""}`}
            style={{
              top: isLandingPill ? "82px" : "72px",
              maxHeight: `calc(100vh - ${isLandingPill ? "82px" : "72px"})`,
              background: isDark
                ? "rgba(6,6,10,0.96)"
                : "rgba(255,255,255,0.98)",
            }}
          >
            {LANDING_NAV.map((item) => (
              <button
                key={item.label}
                className="ah-drawer-link"
                style={{ color: isDark ? "rgba(255,255,255,0.7)" : "#374151" }}
                onClick={() => scrollToSection(item.sectionId)}
              >
                {item.label}
              </button>
            ))}
            <div className="ah-divider" />
            <Link
              to="/login"
              className="ah-drawer-link"
              style={{
                color: isDark ? "rgba(255,255,255,0.5)" : "#6b7280",
                textDecoration: "none",
              }}
              onClick={() => setMobileNavOpen(false)}
            >
              Log in
            </Link>
            <Link
              to="/register"
              className="ah-drawer-cta"
              onClick={() => setMobileNavOpen(false)}
            >
              Get Start
            </Link>
          </div>

          {mobileNavOpen && (
            <div
              className="fixed inset-0 z-40"
              style={{
                background: "rgba(0,0,0,0.25)",
                backdropFilter: "blur(2px)",
              }}
              onClick={() => setMobileNavOpen(false)}
            />
          )}
        </>
      )}

      {/* Spacer — skipped on the landing page, where the bar deliberately
          overlays the hero rather than reserving a band above it. */}
      {!isImmersiveRoute && !isLanding && (
        <div
          className="h-[72px] w-full shrink-0 pointer-events-none"
          aria-hidden="true"
        />
      )}

      {/* Logout confirmation — reuses the same ConfirmDialog as Profile page */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="Sign Out"
        message="Are you sure you want to sign out? You will need to enter your credentials to access your account again."
        confirmText={isLoggingOut ? "Signing out..." : "Sign Out"}
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => { void handleLogoutConfirm(); }}
        onCancel={handleLogoutCancel}
      />
    </>
  );
}
